// Notifications card on the Settings page — alert on/off toggles plus the
// muted-contracts list and add-mute picker. Extracted from Settings.tsx;
// the parent owns the notification-preferences query/mutation and passes
// down data plus the toggle/mute callbacks (built on its patchPrefs helper).

import { Card } from '../common';
import ToggleSwitch from '../common/ToggleSwitch';
import type { ManagedContract, NotificationPreferences } from '../../types';

interface NotificationPreferencesCardProps {
  notifPrefs: NotificationPreferences | undefined;
  isLoading: boolean;
  isError: boolean;
  isPending: boolean;
  allContracts: Pick<ManagedContract, 'id' | 'title'>[];
  onToggleOverdue: () => void;
  onToggleDueSoon: () => void;
  onToggleInsurance: () => void;
  onRemoveMutedContract: (contractId: string) => void;
  onMuteContract: (contractId: string) => void;
}

export default function NotificationPreferencesCard({
  notifPrefs,
  isLoading,
  isError,
  isPending,
  allContracts,
  onToggleOverdue,
  onToggleDueSoon,
  onToggleInsurance,
  onRemoveMutedContract,
  onMuteContract,
}: NotificationPreferencesCardProps) {
  return (
    // `id` is the deep-link target for the "Manage notification settings"
    // link in alert emails (/settings#notifications); scroll-mt keeps the
    // heading clear of the sticky top bar.
    <Card id="notifications" className="scroll-mt-24">
      <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
        Notifications
      </h2>

      {isLoading ? (
        <p className="text-body-sm text-slate-500 dark:text-slate-400">
          Loading notification preferences...
        </p>
      ) : isError ? (
        <p className="text-body-sm text-red-600 dark:text-red-400">
          Failed to load notification preferences.
        </p>
      ) : (
        <div className="space-y-5">
          <p className="text-body-sm text-slate-500 dark:text-slate-400">
            Your org admin controls alert frequency (cadence) — these are
            your own on/off choices and contract mutes.
          </p>

          {isPending && (
            <p className="text-caption text-brand-600 dark:text-brand-400">
              Saving...
            </p>
          )}

          {/* Toggle rows */}
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4 text-body-sm text-slate-700 dark:text-slate-300">
              <span>Overdue alerts</span>
              <ToggleSwitch
                checked={notifPrefs?.overdueEnabled ?? false}
                onChange={onToggleOverdue}
                disabled={isPending}
                ariaLabel="Overdue alerts"
              />
            </label>

            <label className="flex items-center justify-between gap-4 text-body-sm text-slate-700 dark:text-slate-300">
              <span>Due-soon reminders</span>
              <ToggleSwitch
                checked={notifPrefs?.dueSoonEnabled ?? false}
                onChange={onToggleDueSoon}
                disabled={isPending}
                ariaLabel="Due-soon reminders"
              />
            </label>

            <label className="flex items-center justify-between gap-4 text-body-sm text-slate-700 dark:text-slate-300">
              <span>Insurance expiry reminders</span>
              <ToggleSwitch
                checked={notifPrefs?.insuranceEnabled ?? false}
                onChange={onToggleInsurance}
                disabled={isPending}
                ariaLabel="Insurance expiry reminders"
              />
            </label>
          </div>

          {/* Muted contracts */}
          <div className="space-y-2">
            <p className="label">Muted contracts</p>
            {notifPrefs && notifPrefs.mutedContractIds.length > 0 ? (
              <ul className="space-y-1">
                {notifPrefs.mutedContractIds.map((id) => {
                  const contract = allContracts.find((c) => c.id === id);
                  const title = contract?.title ?? id;
                  return (
                    <li
                      key={id}
                      className="flex items-center justify-between gap-2 text-body-sm text-slate-700 dark:text-slate-300"
                    >
                      <span>{title}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${title}`}
                        onClick={() => onRemoveMutedContract(id)}
                        disabled={isPending}
                        className="text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ×
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-body-sm text-slate-500 dark:text-slate-400 italic">
                No contracts muted.
              </p>
            )}

            {/* Add contract picker — excludes already-muted contracts */}
            {allContracts.length > 0 && (
              <div className="mt-2">
                <label htmlFor="mute-contract-picker" className="sr-only">
                  Add contract to mute
                </label>
                <select
                  id="mute-contract-picker"
                  aria-label="Add contract to mute"
                  className="input text-body-sm"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) onMuteContract(e.target.value);
                  }}
                  disabled={isPending}
                >
                  <option value="">Add contract to mute…</option>
                  {allContracts
                    .filter((c) => !notifPrefs?.mutedContractIds.includes(c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
